import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useCompanyPlan } from '../../hooks/useCompanyPlan';
import Layout from '../../components/Layout';
import {
  Plus, Send, ChevronRight,
} from 'lucide-react';

const CATEGORIES = [
  { v: 'billing',      l: '💳 Billing & Plan' },
  { v: 'technical',    l: '🔧 Technical Issue' },
  { v: 'account',      l: '👤 Account Management' },
  { v: 'job_posting',  l: '📋 Job Posting' },
  { v: 'applications', l: '👥 Applications' },
  { v: 'other',        l: '💬 Other' },
];

const PRIORITY = [
  { v: 'low',    l: 'Low',       c: '#94A3B8' },
  { v: 'medium', l: 'Medium',    c: '#F59E0B' },
  { v: 'high',   l: 'High',      c: '#EF4444' },
  { v: 'urgent', l: 'Urgent 🚨', c: '#DC2626' },
];

const TICKET_STATUS = {
  open:            { l: 'Open',               c: '#4F8EF7', bg: '#EFF6FF' },
  in_progress:     { l: 'In Progress',        c: '#7C3AED', bg: '#F5F3FF' },
  waiting_company: { l: 'Awaiting Your Reply', c: '#F59E0B', bg: '#FEF3C7' },
  resolved:        { l: 'Resolved ✓',         c: '#10B981', bg: '#D1FAE5' },
  closed:          { l: 'Closed',             c: '#94A3B8', bg: '#F1F5F9' },
};

export default function Support() {
  const { company, refreshCompany } = useAuth();
  const { plan, can } = useCompanyPlan(company, refreshCompany);

  // ── Fix: plan can be string OR object ────────────────
  const planName = typeof plan === 'string'
    ? plan
    : (plan?.name || plan?.plan_name || 'trial');
  const isEnterprise = planName === 'enterprise';

  const [assignment,        setAssignment]        = useState(null);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [tickets,           setTickets]           = useState([]);
  const [selectedTicket,    setSelectedTicket]    = useState(null);
  const [messages,          setMessages]          = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [view,              setView]              = useState('home');
  const [newTicket,         setNewTicket]         = useState({
    title: '', description: '', category: 'technical', priority: 'medium',
  });
  const [newMessage,  setNewMessage]  = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [successMsg,  setSuccessMsg]  = useState('');
  const [errorMsg,    setErrorMsg]    = useState('');

  useEffect(() => {
    if (company) load();
  }, [company]); // eslint-disable-line

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const showError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(''), 4000);
  };

  // ── Load all support data ────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      // 1. Current assignment
      const { data: assign } = await supabase
        .from('company_manager_assignments')
        .select('*, managers(*)')
        .eq('company_id', company.id)
        .eq('is_active', true)
        .maybeSingle();

      setAssignment(assign);

      // 2. Available managers filtered by tier
      const tier = isEnterprise ? 'enterprise' : 'standard';
      const { data: allManagers } = await supabase
        .from('managers')
        .select('*')
        .eq('is_active', true)
        .eq('tier', tier);

      // 3. Count assigned companies per manager
      const { data: allAssignments } = await supabase
        .from('company_manager_assignments')
        .select('manager_id')
        .eq('is_active', true);

      const counts = {};
      (allAssignments || []).forEach(a => {
        counts[a.manager_id] = (counts[a.manager_id] || 0) + 1;
      });

      const available = (allManagers || []).map(m => ({
        ...m,
        current_companies: counts[m.id] || 0,
        is_available: (counts[m.id] || 0) < m.max_companies,
      }));

      setAvailableManagers(available);

      // 4. Company tickets
      if (assign) {
        const { data: ticketsData } = await supabase
          .from('support_tickets')
          .select('*')
          .eq('company_id', company.id)
          .order('created_at', { ascending: false });
        setTickets(ticketsData || []);
      } else {
        setTickets([]);
      }
    } catch (err) {
      console.error('Support load error:', err);
      showError('Failed to load support data.');
    } finally {
      setLoading(false);
    }
  };

  // ── Choose / change manager ──────────────────────────
  const chooseManager = async (managerId) => {
    setSubmitting(true);
    try {
      // Deactivate old assignment
      await supabase
        .from('company_manager_assignments')
        .update({ is_active: false })
        .eq('company_id', company.id);

      // New assignment
      const { error } = await supabase
        .from('company_manager_assignments')
        .insert({
          company_id: company.id,
          manager_id: managerId,
          is_active: true,
        });

      if (error) throw error;

      await load();
      setView('home');
      showSuccess('✅ Manager assigned! They will be in touch soon.');
    } catch (err) {
      console.error('chooseManager error:', err);
      showError('Failed to assign manager. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Submit new ticket ────────────────────────────────
  const submitTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.description.trim()) return;
    if (!assignment?.manager_id) {
      showError('Please choose a manager first.');
      return;
    }
    setSubmitting(true);
    try {
      const { data: ticket, error } = await supabase
        .from('support_tickets')
        .insert({
          company_id:         company.id,
          manager_id:         assignment.manager_id,
          submitted_by_name:  company.contact_name || company.name,
          submitted_by_email: company.contact_email || '',
          title:              newTicket.title.trim(),
          description:        newTicket.description.trim(),
          category:           newTicket.category,
          priority:           newTicket.priority,
          status:             'open',
        })
        .select()
        .single();

      if (error) throw error;

      // First message = the description
      await supabase.from('ticket_messages').insert({
        ticket_id:   ticket.id,
        sender_type: 'company',
        sender_name: company.name,
        sender_id:   company.id,
        message:     newTicket.description.trim(),
      });

      setNewTicket({
        title: '', description: '', category: 'technical', priority: 'medium',
      });
      await load();
      setView('home');
      showSuccess('✅ Ticket submitted! Your manager will respond soon.');
    } catch (err) {
      console.error('submitTicket error:', err);
      showError('Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Open ticket thread ───────────────────────────────
  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setView('ticket');
    const { data } = await supabase
      .from('ticket_messages')
      .select('*')
      .eq('ticket_id', ticket.id)
      .order('created_at', { ascending: true });
    setMessages(data || []);
  };

  // ── Send message in thread ───────────────────────────
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    try {
      await supabase.from('ticket_messages').insert({
        ticket_id:   selectedTicket.id,
        sender_type: 'company',
        sender_name: company.name,
        sender_id:   company.id,
        message:     newMessage.trim(),
      });
      await supabase
        .from('support_tickets')
        .update({
          status:     'in_progress',
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedTicket.id);

      setNewMessage('');
      await openTicket(selectedTicket);
    } catch (err) {
      console.error('sendMessage error:', err);
    }
  };

  const manager = assignment?.managers;

  // ── Loading state ────────────────────────────────────
  if (loading) {
    return (
      <Layout>
        <div style={{
          display: 'flex', justifyContent: 'center',
          alignItems: 'center', padding: 80, gap: 12,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            border: '3px solid #E2E8F0', borderTopColor: '#4F8EF7',
            animation: 'spin 0.8s linear infinite',
          }} />
          <span style={{ color: '#94A3B8', fontSize: 14 }}>Loading support...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>

      {/* ── Toast notifications ── */}
      {successMsg && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: '#F0FDF4', border: '1px solid #BBF7D0',
          borderRadius: 12, padding: '12px 20px',
          fontSize: 13, fontWeight: 600, color: '#16A34A',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: '#FFF1F2', border: '1px solid #FECDD3',
          borderRadius: 12, padding: '12px 20px',
          fontSize: 13, fontWeight: 600, color: '#E11D48',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          {errorMsg}
        </div>
      )}

      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* ── Page header ── */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1E293B' }}>
            {isEnterprise ? '⚡ Enterprise Support' : '🛎️ Support Center'}
          </h1>
          <p style={{ color: '#94A3B8', fontSize: 14, marginTop: 4 }}>
            {isEnterprise
              ? 'Dedicated enterprise manager — priority same-day response'
              : 'Your dedicated support manager — response within 24 hours'}
          </p>
        </div>

        {/* ── Plan badge ── */}
        <div style={{
          background: isEnterprise
            ? 'linear-gradient(135deg,rgba(245,158,11,0.08),rgba(239,68,68,0.06))'
            : 'linear-gradient(135deg,rgba(79,142,247,0.06),rgba(124,58,237,0.04))',
          border: `1px solid ${isEnterprise
            ? 'rgba(245,158,11,0.3)' : 'rgba(79,142,247,0.2)'}`,
          borderRadius: 14, padding: '14px 20px', marginBottom: 24,
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <span style={{ fontSize: 28 }}>
            {isEnterprise ? '👑' : planName === 'pro' ? '🚀' : '✨'}
          </span>
          <div>
            <div style={{
              fontWeight: 700, fontSize: 14,
              color: isEnterprise ? '#F59E0B' : '#4F8EF7',
            }}>
              {/* ✅ Safe toUpperCase — planName is always a string */}
              {planName.toUpperCase()} Plan Support
            </div>
            <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
              {isEnterprise
                ? 'Dedicated enterprise manager · Priority queue · Same-day response · Direct escalation to admin'
                : 'Dedicated account manager · 24h response · Full ticket tracking · Issue escalation'}
            </div>
          </div>
        </div>

        {/* ── No manager assigned ── */}
        {!assignment ? (
          view === 'choose' ? (
            <ChooseManager
              managers={availableManagers}
              isEnterprise={isEnterprise}
              onChoose={chooseManager}
              onBack={() => setView('home')}
              submitting={submitting}
            />
          ) : (
            <div style={{
              background: '#fff', borderRadius: 18,
              border: '1px solid #E2E8F0', padding: 40,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>👋</div>
              <div style={{
                fontSize: 20, fontWeight: 800, color: '#1E293B', marginBottom: 8,
              }}>
                Choose Your Support Manager
              </div>
              <div style={{
                fontSize: 14, color: '#94A3B8', marginBottom: 28, lineHeight: 1.6,
              }}>
                Select a dedicated manager who will handle all your support needs.
                {isEnterprise
                  ? ' Enterprise managers handle max 10 companies for priority service.'
                  : ' Standard managers handle up to 30 companies.'}
              </div>
              <button onClick={() => setView('choose')} style={{
                padding: '14px 32px', borderRadius: 12, border: 'none',
                background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                color: '#fff', fontWeight: 700, fontSize: 15,
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(79,142,247,0.3)',
              }}>
                View Available Managers →
              </button>
            </div>
          )
        ) : (
          <>
            {/* ── Home view: manager card + stats ── */}
            {view === 'home' && (
              <>
                <div style={{ display: 'flex', gap: 20 }}>
                  {/* Manager card */}
                  <div style={{ flex: 1 }}>
                    <ManagerCard manager={manager} isEnterprise={isEnterprise} />
                    <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                      <button onClick={() => setView('new')} style={{
                        flex: 1, padding: '13px 0', borderRadius: 10, border: 'none',
                        background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                        color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 8,
                      }}>
                        <Plus size={16} /> New Support Ticket
                      </button>
                      <button onClick={() => setView('choose')} style={{
                        padding: '13px 16px', borderRadius: 10,
                        border: '1px solid #E2E8F0', background: '#fff',
                        color: '#64748B', fontWeight: 600, fontSize: 13,
                        cursor: 'pointer',
                      }}>
                        Change
                      </button>
                    </div>
                  </div>

                  {/* Quick stats */}
                  <div style={{
                    width: 200, display: 'flex', flexDirection: 'column', gap: 12,
                  }}>
                    {[
                      { l: 'Total Tickets', v: tickets.length, i: '📋', c: '#4F8EF7' },
                      {
                        l: 'Open', i: '🔔', c: '#F59E0B',
                        v: tickets.filter(t =>
                          ['open', 'in_progress', 'waiting_company'].includes(t.status)
                        ).length,
                      },
                      {
                        l: 'Resolved', i: '✅', c: '#10B981',
                        v: tickets.filter(t => t.status === 'resolved').length,
                      },
                    ].map((s, i) => (
                      <div key={i} style={{
                        background: '#fff', borderRadius: 12,
                        border: '1px solid #E2E8F0', padding: '14px 16px',
                        display: 'flex', alignItems: 'center', gap: 10,
                      }}>
                        <span style={{ fontSize: 22 }}>{s.i}</span>
                        <div>
                          <div style={{
                            fontSize: 20, fontWeight: 800, color: s.c,
                          }}>{s.v}</div>
                          <div style={{ fontSize: 10, color: '#94A3B8' }}>{s.l}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Ticket list ── */}
                {tickets.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <h2 style={{
                      fontSize: 16, fontWeight: 700,
                      color: '#1E293B', marginBottom: 14,
                    }}>
                      Your Tickets
                    </h2>
                    <div style={{
                      display: 'flex', flexDirection: 'column', gap: 10,
                    }}>
                      {tickets.map(t => {
                        const ts = TICKET_STATUS[t.status] || TICKET_STATUS.open;
                        return (
                          <div key={t.id}
                            onClick={() => openTicket(t)}
                            onMouseEnter={e =>
                              e.currentTarget.style.borderColor = '#4F8EF7'}
                            onMouseLeave={e =>
                              e.currentTarget.style.borderColor = '#E2E8F0'}
                            style={{
                              background: '#fff', borderRadius: 12,
                              padding: '16px 20px', border: '1px solid #E2E8F0',
                              cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 14,
                              transition: 'border-color 0.15s',
                            }}>
                            {/* Category icon */}
                            <div style={{
                              width: 40, height: 40, borderRadius: '50%',
                              flexShrink: 0, background: `${ts.c}15`,
                              display: 'flex', alignItems: 'center',
                              justifyContent: 'center', fontSize: 18,
                            }}>
                              {t.category === 'billing'      ? '💳'
                                : t.category === 'technical' ? '🔧'
                                : t.category === 'job_posting' ? '📋'
                                : t.category === 'applications' ? '👥'
                                : '💬'}
                            </div>

                            {/* Info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontWeight: 700, fontSize: 14, color: '#1E293B',
                              }}>
                                {t.title}
                              </div>
                              <div style={{
                                fontSize: 11, color: '#94A3B8', marginTop: 2,
                              }}>
                                {new Date(t.created_at).toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric', year: 'numeric',
                                })}
                                {' · '}
                                {CATEGORIES.find(c => c.v === t.category)?.l || t.category}
                              </div>
                            </div>

                            {/* Status + chevron */}
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                            }}>
                              <span style={{
                                padding: '4px 10px', borderRadius: 6,
                                fontSize: 11, fontWeight: 700,
                                background: ts.bg, color: ts.c,
                              }}>
                                {ts.l}
                              </span>
                              {t.priority === 'urgent' && (
                                <span style={{
                                  fontSize: 9, fontWeight: 800,
                                  padding: '2px 6px', borderRadius: 4,
                                  background: '#FFF1F2', color: '#EF4444',
                                }}>
                                  URGENT
                                </span>
                              )}
                              <ChevronRight size={16} color="#94A3B8" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty tickets */}
                {tickets.length === 0 && (
                  <div style={{
                    marginTop: 24, background: '#F8FAFF',
                    borderRadius: 14, border: '1px dashed #E2E8F0',
                    padding: '40px 20px', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 36, marginBottom: 12 }}>🎫</div>
                    <div style={{
                      fontWeight: 700, fontSize: 15, color: '#64748B', marginBottom: 6,
                    }}>
                      No tickets yet
                    </div>
                    <div style={{ fontSize: 13, color: '#94A3B8' }}>
                      Submit a support ticket and your manager will respond within{' '}
                      {isEnterprise ? 'the same day' : '24 hours'}.
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── New ticket form ── */}
            {view === 'new' && (
              <div style={{
                background: '#fff', borderRadius: 18,
                border: '1px solid #E2E8F0', padding: 28,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', marginBottom: 24,
                }}>
                  <div>
                    <h2 style={{
                      fontSize: 18, fontWeight: 800, color: '#1E293B', margin: 0,
                    }}>
                      New Support Ticket
                    </h2>
                    <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
                      Assigned to: <strong>{manager?.name}</strong>
                    </div>
                  </div>
                  <button onClick={() => setView('home')} style={{
                    border: 'none', background: 'none',
                    color: '#94A3B8', cursor: 'pointer', fontSize: 22,
                  }}>×</button>
                </div>

                {/* Category + Priority */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr',
                  gap: 16, marginBottom: 16,
                }}>
                  <div>
                    <label style={{
                      display: 'block', fontSize: 12, fontWeight: 700,
                      color: '#64748B', marginBottom: 8, letterSpacing: 0.5,
                    }}>
                      CATEGORY
                    </label>
                    <select
                      value={newTicket.category}
                      onChange={e =>
                        setNewTicket(t => ({ ...t, category: e.target.value }))}
                      style={{
                        width: '100%', padding: '10px 12px',
                        border: '1.5px solid #E2E8F0', borderRadius: 10,
                        fontSize: 13, outline: 'none', background: '#fff',
                      }}>
                      {CATEGORIES.map(c => (
                        <option key={c.v} value={c.v}>{c.l}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{
                      display: 'block', fontSize: 12, fontWeight: 700,
                      color: '#64748B', marginBottom: 8, letterSpacing: 0.5,
                    }}>
                      PRIORITY
                    </label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {PRIORITY.map(p => (
                        <button key={p.v}
                          onClick={() =>
                            setNewTicket(t => ({ ...t, priority: p.v }))}
                          style={{
                            flex: 1, padding: '10px 4px', borderRadius: 8,
                            border: newTicket.priority === p.v
                              ? `2px solid ${p.c}` : '1.5px solid #E2E8F0',
                            background: newTicket.priority === p.v
                              ? `${p.c}15` : '#fff',
                            color: newTicket.priority === p.v ? p.c : '#64748B',
                            fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          }}>
                          {p.l}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Subject */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{
                    display: 'block', fontSize: 12, fontWeight: 700,
                    color: '#64748B', marginBottom: 6, letterSpacing: 0.5,
                  }}>
                    SUBJECT *
                  </label>
                  <input
                    value={newTicket.title}
                    onChange={e =>
                      setNewTicket(t => ({ ...t, title: e.target.value }))}
                    placeholder="Brief description of your issue..."
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: '1.5px solid #E2E8F0', borderRadius: 10,
                      fontSize: 13, outline: 'none',
                    }}
                  />
                </div>

                {/* Description */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{
                    display: 'block', fontSize: 12, fontWeight: 700,
                    color: '#64748B', marginBottom: 6, letterSpacing: 0.5,
                  }}>
                    DESCRIPTION *
                  </label>
                  <textarea
                    value={newTicket.description}
                    onChange={e =>
                      setNewTicket(t => ({ ...t, description: e.target.value }))}
                    placeholder="Describe your issue in detail. Include any error messages, steps to reproduce, or what you expected to happen..."
                    rows={5}
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: '1.5px solid #E2E8F0', borderRadius: 10,
                      fontSize: 13, resize: 'none', outline: 'none', lineHeight: 1.6,
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setView('home')} style={{
                    flex: 1, padding: '12px 0', borderRadius: 10,
                    border: '1px solid #E2E8F0', background: '#fff',
                    color: '#64748B', fontWeight: 600, cursor: 'pointer',
                  }}>
                    Cancel
                  </button>
                  <button
                    onClick={submitTicket}
                    disabled={
                      submitting ||
                      !newTicket.title.trim() ||
                      !newTicket.description.trim()
                    }
                    style={{
                      flex: 2, padding: '12px 0', borderRadius: 10, border: 'none',
                      background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                      color: '#fff', fontWeight: 700, fontSize: 14,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      opacity: (submitting ||
                        !newTicket.title.trim() ||
                        !newTicket.description.trim()) ? 0.6 : 1,
                    }}>
                    {submitting ? 'Submitting...' : '📤 Submit Ticket'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Ticket thread view ── */}
            {view === 'ticket' && selectedTicket && (
              <div style={{
                background: '#fff', borderRadius: 18,
                border: '1px solid #E2E8F0', overflow: 'hidden',
              }}>
                {/* Header */}
                <div style={{
                  padding: '20px 24px', borderBottom: '1px solid #F1F5F9',
                  display: 'flex', alignItems: 'flex-start', gap: 16,
                }}>
                  <button
                    onClick={() => { setView('home'); setSelectedTicket(null); }}
                    style={{
                      border: 'none', background: '#F1F5F9', borderRadius: 8,
                      padding: '6px 12px', cursor: 'pointer',
                      color: '#64748B', fontSize: 12, fontWeight: 600,
                    }}>
                    ← Back
                  </button>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 800, fontSize: 16, color: '#1E293B',
                    }}>
                      {selectedTicket.title}
                    </div>
                    <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
                      Ticket #{selectedTicket.id.slice(0, 8)}
                      {' · '}
                      Opened {new Date(selectedTicket.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <span style={{
                    padding: '5px 12px', borderRadius: 8,
                    fontSize: 12, fontWeight: 700,
                    background:
                      TICKET_STATUS[selectedTicket.status]?.bg || '#F1F5F9',
                    color:
                      TICKET_STATUS[selectedTicket.status]?.c || '#94A3B8',
                  }}>
                    {TICKET_STATUS[selectedTicket.status]?.l || selectedTicket.status}
                  </span>
                </div>

                {/* Messages */}
                <div style={{
                  padding: 24, maxHeight: 420, overflowY: 'auto',
                  display: 'flex', flexDirection: 'column', gap: 16,
                }}>
                  {messages.length === 0 ? (
                    <div style={{
                      textAlign: 'center', color: '#94A3B8', fontSize: 13, padding: 20,
                    }}>
                      No messages yet.
                    </div>
                  ) : messages.map(msg => (
                    <div key={msg.id} style={{
                      display: 'flex',
                      justifyContent: msg.sender_type === 'company'
                        ? 'flex-end' : 'flex-start',
                    }}>
                      <div style={{
                        maxWidth: '72%',
                        background: msg.sender_type === 'company'
                          ? 'linear-gradient(135deg,#4F8EF7,#7C3AED)'
                          : '#F8FAFF',
                        color: msg.sender_type === 'company' ? '#fff' : '#1E293B',
                        borderRadius: msg.sender_type === 'company'
                          ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        padding: '12px 16px',
                        border: msg.sender_type === 'company'
                          ? 'none' : '1px solid #E2E8F0',
                      }}>
                        <div style={{
                          fontSize: 10, fontWeight: 700, marginBottom: 6,
                          color: msg.sender_type === 'company'
                            ? 'rgba(255,255,255,0.75)' : '#94A3B8',
                        }}>
                          {msg.sender_name ||
                            (msg.sender_type === 'manager' ? 'Support Manager' : 'You')}
                          {' · '}
                          {new Date(msg.created_at).toLocaleTimeString('en-US', {
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </div>
                        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                          {msg.message}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply input */}
                {!['resolved', 'closed'].includes(selectedTicket.status) && (
                  <div style={{
                    padding: '14px 24px', borderTop: '1px solid #F1F5F9',
                    display: 'flex', gap: 10,
                  }}>
                    <input
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder="Type your reply... (Enter to send)"
                      style={{
                        flex: 1, padding: '10px 14px',
                        border: '1.5px solid #E2E8F0', borderRadius: 10,
                        fontSize: 13, outline: 'none',
                      }}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      style={{
                        padding: '10px 18px', borderRadius: 10, border: 'none',
                        background: newMessage.trim()
                          ? 'linear-gradient(135deg,#4F8EF7,#7C3AED)'
                          : '#E2E8F0',
                        color: newMessage.trim() ? '#fff' : '#94A3B8',
                        cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                        display: 'flex', alignItems: 'center', gap: 6,
                        fontWeight: 600, fontSize: 13,
                      }}>
                      <Send size={14} /> Send
                    </button>
                  </div>
                )}

                {['resolved', 'closed'].includes(selectedTicket.status) && (
                  <div style={{
                    padding: '14px 24px', borderTop: '1px solid #F1F5F9',
                    textAlign: 'center', fontSize: 13, color: '#94A3B8',
                  }}>
                    This ticket is {selectedTicket.status}. Open a new ticket if you need further help.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

// ── Manager Card component ────────────────────────────
function ManagerCard({ manager, isEnterprise }) {
  if (!manager) return null;
  return (
    <div style={{
      background: isEnterprise
        ? 'linear-gradient(135deg,rgba(245,158,11,0.06),rgba(239,68,68,0.04))'
        : 'linear-gradient(135deg,rgba(79,142,247,0.06),rgba(124,58,237,0.04))',
      border: `1px solid ${isEnterprise
        ? 'rgba(245,158,11,0.25)' : 'rgba(79,142,247,0.2)'}`,
      borderRadius: 16, padding: 20,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: manager.avatar_color || '#4F8EF7',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 900, fontSize: 22,
          boxShadow: `0 4px 14px ${manager.avatar_color || '#4F8EF7'}40`,
        }}>
          {(manager.name || 'M')[0]}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#1E293B' }}>
              {manager.name}
            </div>
            {isEnterprise && (
              <span style={{
                padding: '2px 8px', borderRadius: 4,
                background: 'rgba(245,158,11,0.15)', color: '#F59E0B',
                fontSize: 9, fontWeight: 800,
              }}>
                ENTERPRISE
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
            {manager.specialization || 'Support Specialist'}
          </div>
        </div>
        {/* Online indicator */}
        <div style={{
          width: 10, height: 10, borderRadius: '50%',
          background: '#10B981',
          boxShadow: '0 0 0 3px rgba(16,185,129,0.2)',
        }} />
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{
          flex: 1, background: 'rgba(255,255,255,0.7)',
          borderRadius: 10, padding: '10px 14px',
        }}>
          <div style={{
            fontSize: 10, color: '#94A3B8',
            fontWeight: 700, marginBottom: 3,
          }}>
            RESPONSE TIME
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>
            ⏱ {manager.response_time || 'Within 24 hours'}
          </div>
        </div>
        <div style={{
          flex: 1, background: 'rgba(255,255,255,0.7)',
          borderRadius: 10, padding: '10px 14px',
        }}>
          <div style={{
            fontSize: 10, color: '#94A3B8',
            fontWeight: 700, marginBottom: 3,
          }}>
            AVAILABILITY
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#10B981' }}>
            ✅ Available 24/7
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Choose Manager component ──────────────────────────
function ChooseManager({ managers, isEnterprise, onChoose, onBack, submitting }) {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
      }}>
        <button onClick={onBack} style={{
          border: 'none', background: '#F1F5F9', borderRadius: 8,
          padding: '7px 12px', cursor: 'pointer',
          color: '#64748B', fontSize: 13, fontWeight: 600,
        }}>
          ← Back
        </button>
        <div>
          <h2 style={{
            fontSize: 18, fontWeight: 800, color: '#1E293B', margin: 0,
          }}>
            Choose Your Manager
          </h2>
          <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
            {isEnterprise
              ? 'Enterprise managers handle max 10 companies — priority response'
              : 'Standard managers handle up to 30 companies'}
          </div>
        </div>
      </div>

      {managers.length === 0 ? (
        <div style={{
          background: '#FFF1F2', border: '1px solid #FECDD3',
          borderRadius: 14, padding: 32, textAlign: 'center',
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>😔</div>
          <div style={{ fontWeight: 700, color: '#9F1239', marginBottom: 6 }}>
            No managers available right now
          </div>
          <div style={{ fontSize: 13, color: '#BE123C' }}>
            All {isEnterprise ? 'enterprise' : 'standard'} managers are at capacity.
            Please contact admin@inncatechnologies.com for assistance.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {managers.map(m => (
            <div key={m.id} style={{
              background: '#fff', borderRadius: 16, padding: 20,
              border: m.is_available
                ? '1.5px solid #E2E8F0' : '1.5px solid #F1F5F9',
              opacity: m.is_available ? 1 : 0.55,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {/* Avatar */}
                <div style={{
                  width: 54, height: 54, borderRadius: '50%', flexShrink: 0,
                  background: m.avatar_color || '#4F8EF7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 900, fontSize: 20,
                  boxShadow: m.is_available
                    ? `0 4px 14px ${m.avatar_color || '#4F8EF7'}40` : 'none',
                }}>
                  {(m.name || 'M')[0]}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      fontWeight: 800, fontSize: 15, color: '#1E293B',
                    }}>
                      {m.name}
                    </div>
                    {!m.is_available && (
                      <span style={{
                        fontSize: 9, fontWeight: 800, padding: '2px 6px',
                        borderRadius: 4, background: '#FEF3C7', color: '#F59E0B',
                      }}>
                        FULL
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
                    {m.specialization}
                  </div>
                  <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: '#64748B' }}>
                      ⏱ {m.response_time}
                    </span>
                    <span style={{ fontSize: 11, color: '#64748B' }}>
                      👥 {m.current_companies}/{m.max_companies} companies
                    </span>
                    <span style={{
                      fontSize: 11,
                      color: m.is_available ? '#10B981' : '#EF4444',
                      fontWeight: 600,
                    }}>
                      {m.is_available
                        ? `${m.max_companies - m.current_companies} slots left`
                        : 'No slots available'}
                    </span>
                  </div>
                </div>

                {/* Select button */}
                <button
                  onClick={() => m.is_available && onChoose(m.id)}
                  disabled={!m.is_available || submitting}
                  style={{
                    padding: '10px 20px', borderRadius: 10, border: 'none',
                    flexShrink: 0,
                    background: m.is_available
                      ? `linear-gradient(135deg,${m.avatar_color || '#4F8EF7'},${m.avatar_color || '#4F8EF7'}cc)`
                      : '#E2E8F0',
                    color: m.is_available ? '#fff' : '#94A3B8',
                    fontWeight: 700, fontSize: 13,
                    cursor: m.is_available && !submitting ? 'pointer' : 'not-allowed',
                    opacity: submitting ? 0.6 : 1,
                    transition: 'opacity 0.2s',
                  }}>
                  {submitting ? '...' : m.is_available ? 'Select' : 'Full'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}